import { motion } from "framer-motion";
import * as Icons from "lucide-react";
import { Reveal, Overline } from "./Reveal";
import { STATS, WHY } from "../data";

export const Stats = () => (
  <section className="bg-navy relative -mt-1" data-testid="stats">
    <div className="max-w-7xl mx-auto px-6 py-14 grid grid-cols-2 lg:grid-cols-3 gap-y-10 gap-x-6 border-t border-white/10">
      {STATS.map((s, i) => (
        <Reveal key={s.label} delay={i * 0.08}>
          <div className="text-center lg:text-left">
            <div className="font-display font-light text-4xl sm:text-5xl text-white">{s.value}</div>
            <div className="mt-2 text-white/55 text-xs sm:text-sm tracking-wide">{s.label}</div>
          </div>
        </Reveal>
      ))}
    </div>
  </section>
);

export const WhyEagle = ({ heading = true }) => (
  <section id="why" className="py-24 lg:py-32 bg-white" data-testid="why">
    <div className="max-w-7xl mx-auto px-6">
      {heading && (
        <div className="grid lg:grid-cols-12 gap-10 items-end mb-16">
          <div className="lg:col-span-7">
            <Reveal><Overline>Why Eagle 1</Overline></Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-5 font-display font-light text-4xl md:text-5xl tracking-tight text-[var(--primary)] leading-[1.05]">
                A flight school where <span className="italic text-accent">tomorrow's pilots</span> are made.
              </h2>
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <Reveal delay={0.15}>
              <p className="text-[var(--muted)] text-base md:text-lg leading-relaxed">
                We train to the highest standards — safety, airmanship and professionalism first — turning committed students into disciplined, career-ready pilots.
              </p>
            </Reveal>
          </div>
        </div>
      )}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {WHY.map((c, i) => {
          const Icon = Icons[c.icon] || Icons.Plane;
          return (
            <Reveal key={c.title} delay={i * 0.1}>
              <div className="group h-full rounded-2xl border border-[var(--line)] bg-white p-8 card-shadow hover:-translate-y-2 transition-transform duration-500" data-testid={`why-card-${i}`}>
                <div className="h-14 w-14 rounded-2xl bg-navy flex items-center justify-center group-hover:bg-accent-c transition-colors duration-500">
                  <Icon className="text-accent-2 group-hover:text-white transition-colors duration-500" size={26} />
                </div>
                <h3 className="mt-6 font-display text-xl font-semibold text-[var(--primary)]">{c.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-[var(--muted)]">{c.text}</p>
              </div>
            </Reveal>
          );
        })}
      </div>
    </div>
  </section>
);

export const MarqueeStrip = () => {
  const ease = [0.22, 1, 0.36, 1];
  return (
    <section className="relative bg-navy overflow-hidden py-20 lg:py-24" data-testid="flight-divider">
      {/* ambient glows */}
      <div className="absolute -top-24 left-1/4 h-72 w-72 rounded-full bg-[var(--secondary)] opacity-20 blur-[130px]" />
      <div className="absolute -bottom-24 right-1/4 h-72 w-72 rounded-full bg-accent-c opacity-10 blur-[130px]" />
      <div className="absolute inset-0 grain opacity-40" />

      <div className="relative max-w-6xl mx-auto px-6">
        <div className="flex items-center justify-between gap-4 sm:gap-8">
          {/* left anchor — the training */}
          <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, ease }} className="shrink-0 text-left">
            <div className="overline text-accent-2">The Training</div>
            <div className="mt-1.5 font-display font-light text-white text-base sm:text-2xl leading-tight">Every rating,<br />mastered.</div>
          </motion.div>

          {/* animated flight path */}
          <div className="relative flex-1 h-9">
            <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 border-t border-dashed border-white/20" />
            <motion.div initial={{ scaleX: 0 }} whileInView={{ scaleX: 1 }} viewport={{ once: true }} transition={{ duration: 1.6, ease }} style={{ transformOrigin: "left" }} className="absolute inset-x-0 top-1/2 -translate-y-1/2 border-t-2 border-accent-2/70" />
            <span className="absolute left-0 top-1/2 -translate-y-1/2 h-2.5 w-2.5 rounded-full bg-accent-2 ring-4 ring-accent-2/20" />
            <span className="absolute right-0 top-1/2 -translate-y-1/2 h-2.5 w-2.5 rounded-full bg-accent-2 ring-4 ring-accent-2/20" />
            <motion.div initial={{ left: "0%" }} whileInView={{ left: "100%" }} viewport={{ once: true }} transition={{ duration: 1.6, ease }} className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2">
              <div className="h-9 w-9 rounded-full bg-white flex items-center justify-center shadow-lg">
                <Icons.Plane size={18} className="text-[var(--primary)] rotate-90" />
              </div>
            </motion.div>
          </div>

          {/* right anchor — the fleet */}
          <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.2, ease }} className="shrink-0 text-right">
            <div className="overline text-accent-2">The Fleet</div>
            <div className="mt-1.5 font-display font-light text-white text-base sm:text-2xl leading-tight">Ready when<br />you are.</div>
          </motion.div>
        </div>

        <motion.p initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.5, ease }} className="mt-9 text-center text-white/45 text-[11px] sm:text-sm tracking-[0.3em] uppercase">
          {"Where tomorrow's pilots take flight"}
        </motion.p>
      </div>
    </section>
  );
};
